import '@mdxeditor/editor/style.css'

import { PrimaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ArrowLeft from 'components/ReusableComponents/Icons/ArrowLeft'
import TextArea from 'components/ReusableComponents/ManageHotel/TextArea'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { ColorInput, ImageFileInput } from 'features/ConfigureWidgets'
import {
  propertyNames,
  propertyTypes,
  sectionsToProperties
} from 'features/EmailEditor/constants'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import RichTextEditor from 'features/EmailEditor/RichTextEditor'
import { getTemplateOptions } from 'features/EmailEditor/templates'
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark'
import { useToasts } from 'react-toast-notifications'

import styles from './EmailEditor.module.scss'

const EmailEditorContent = () => {
  const {
    editingSection,
    setEditingSection,
    selectedTemplate,
    hoveredPreview,
    setHoveredPreview,
    mousePosition,
    setMousePosition,
    emailState,
    currentUser,
    updateEmailState,
    updateRichTextField,
    handleTemplateChange,
    handleSocialMediaChange,
    saveChanges,
    sendTestEmail
  } = useEmailEditor()

  const { addToast } = useToasts()

  const handleSendTestEmail = () => {
    saveChanges()

    setTimeout(() => {
      sendTestEmail()
        .then(() => {
          addToast('Test email sent successfully!', { appearance: 'success' })
        })
        .catch(() => {
          addToast('Failed to send test email.', { appearance: 'error' })
        })
    }, 500)
  }

  return (
    <div>
      <div className={styles.editorWrapper}>
        <div className={styles.inputs}>
          <div className={styles.inputsHeader}>
            {editingSection && (
              <>
                <button
                  className={styles.backButton}
                  onClick={() => setEditingSection(null)}
                >
                  <ArrowLeft /> <span>Back to email settings</span>
                </button>
                <span className={style.sectionTitle}>Editing {editingSection}</span>
              </>
            )}
            {!editingSection && (
              <span className={style.sectionTitle}>Email settings</span>
            )}
          </div>
          <div className={styles.inputsInner}>
            {!editingSection && (
              <div className={styles.inputGroup}>
                <label
                  style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block' }}
                >
                  Email Template:
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '16px',
                    marginBottom: '12px'
                  }}
                >
                  {getTemplateOptions().map(option => (
                    <div
                      key={option.value}
                      onClick={() => handleTemplateChange(option.value)}
                      style={{
                        cursor: 'pointer',
                        border:
                          selectedTemplate === option.value
                            ? '3px solid #0079ff'
                            : '2px solid #ddd',
                        borderRadius: '12px',
                        padding: '0',
                        backgroundColor:
                          selectedTemplate === option.value ? '#f0f8ff' : '#fff',
                        transition: 'all 0.2s ease',
                        textAlign: 'center',
                        boxShadow:
                          selectedTemplate === option.value
                            ? '0 4px 12px rgba(0, 121, 255, 0.2)'
                            : '0 2px 4px rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onMouseEnter={e => {
                        setHoveredPreview(option.preview)
                        setMousePosition({ x: e.clientX, y: e.clientY })
                        if (selectedTemplate !== option.value) {
                          e.currentTarget.style.borderColor = '#0079ff'
                          e.currentTarget.style.backgroundColor = '#f9f9f9'
                          e.currentTarget.style.transform = 'translateY(-2px)'
                          e.currentTarget.style.boxShadow =
                            '0 4px 8px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                      onMouseMove={e => {
                        if (hoveredPreview) {
                          setMousePosition({ x: e.clientX, y: e.clientY })
                        }
                      }}
                      onMouseLeave={e => {
                        setHoveredPreview(null)
                        setMousePosition({ x: 0, y: 0 })
                        if (selectedTemplate !== option.value) {
                          e.currentTarget.style.borderColor = '#ddd'
                          e.currentTarget.style.backgroundColor = '#fff'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      <img
                        src={option.cardImage}
                        alt={option.label}
                        style={{
                          width: '100%',
                          height: '140px',
                          objectFit: 'cover',
                          borderRadius: '12px 12px 0 0',
                          border: 'none'
                        }}
                        onError={e => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.height = '140px'
                          e.target.nextSibling.style.display = 'flex'
                          e.target.nextSibling.style.alignItems = 'center'
                          e.target.nextSibling.style.justifyContent = 'center'
                          e.target.nextSibling.style.backgroundColor = '#f5f5f5'
                          e.target.nextSibling.style.borderRadius = '12px 12px 0 0'
                        }}
                      />
                      <div
                        style={{
                          display: 'none',
                          fontSize: '12px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}
                      >
                        Preview Not Available
                      </div>
                      <div style={{ padding: '12px', textAlign: 'left' }}>
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight:
                              selectedTemplate === option.value ? 'bold' : '600',
                            color: selectedTemplate === option.value ? '#0079ff' : '#333',
                            marginBottom: '6px'
                          }}
                        >
                          {option.label}
                        </div>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#666',
                            lineHeight: '1.3'
                          }}
                        >
                          {option.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email Editor Inputs according to the active section */}
            {sectionsToProperties[editingSection || 'global'].map(property => {
              const type = propertyTypes[property]
              if (type === 'color') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <ColorInput
                      title={propertyNames[property]}
                      height={32}
                      defaultColor={emailState[property]}
                      onChange={value => updateEmailState(property, value)}
                    />
                  </div>
                )
              } else if (type === 'socialmedia') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <span style={{ display: 'inline-block', width: '200px' }}>
                      {propertyNames[property]}:
                    </span>
                    {emailState.socialMediaLinks.map((link, index) => (
                      <TextInput
                        key={index}
                        label={link.platform}
                        value={link.url}
                        onChange={e =>
                          handleSocialMediaChange(index, 'url', e.target.value)
                        }
                      />
                    ))}
                  </div>
                )
              } else if (type === 'image') {
                return (
                  <ImageFileInput
                    kind="drip-emails"
                    title={propertyNames[property]}
                    backgroundSize={property.includes('logo') ? '90%' : 'cover'}
                    defaultUrl={emailState[property] || ''}
                    onChange={text => {
                      updateEmailState(property, text)
                    }}
                    minWidth={200}
                    minHeight={200}
                  />
                )
              } else if (type === 'richtext') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <RichTextEditor
                      label={propertyNames[property]}
                      value={emailState.richtextFields[property] || ''}
                      onChange={value => updateRichTextField(property, value)}
                    />
                  </div>
                )
              } else if (type === 'multilinetext') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <TextArea
                      label={propertyNames[property]}
                      value={emailState.preheaderText}
                      onChange={value => updateEmailState(property, value)}
                      multiline={true}
                    />
                  </div>
                )
              } else {
                // text
                return (
                  <div key={property} className={styles.inputGroup}>
                    <TextInput
                      label={propertyNames[property]}
                      value={emailState[property]}
                      onChangeValue={text => {
                        updateEmailState(property, text)
                      }}
                    />
                  </div>
                )
              }
            })}

            {!editingSection && (
              <div
                style={{
                  borderTop: '1px solid #eee',
                  marginTop: 'auto',
                  paddingTop: '16px'
                }}
              >
                <p>Test your email</p>
                <PrimaryRegularButton onClick={handleSendTestEmail}>
                  Send Test Email
                </PrimaryRegularButton>
                <p>Email will be sent to {currentUser.email}</p>
              </div>
            )}
          </div>
        </div>

        <>
          <EmailPreview />
        </>
      </div>

      {/* Tooltip-style preview */}
      {hoveredPreview && (
        <div
          style={{
            position: 'fixed',
            left: mousePosition.x + 20,
            top: mousePosition.y + 20,
            zIndex: 9999,
            pointerEvents: 'none',
            backgroundColor: 'white',
            border: '2px solid #ddd',
            borderRadius: '8px',
            padding: '8px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            maxWidth: '50vw',
            maxHeight: '50vh'
          }}
        >
          <img
            src={hoveredPreview}
            alt="Template Preview"
            style={{
              width: 'auto',
              height: 'auto',
              maxWidth: '500px',
              maxHeight: '400px',
              objectFit: 'contain',
              borderRadius: '4px',
              display: 'block'
            }}
          />
        </div>
      )}
    </div>
  )
}

// Wrapper component that provides the context
const EmailEditor = () => {
  return (
    <EmailEditorProvider>
      <EmailEditorContent />
    </EmailEditorProvider>
  )
}

export default EmailEditor
